import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ManageBlog = () => {
  const [posts, setPosts] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('posts');
  const [selectedPost, setSelectedPost] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [postsRes, feedsRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/blog/posts`),
        axios.get(`${BACKEND_URL}/api/blog/feeds`),
        axios.get(`${BACKEND_URL}/api/blog/stats`)
      ]);
      setPosts(postsRes.data.posts || []);
      setFeeds(feedsRes.data.feeds || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch blog data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchNews = async () => {
    setFetching(true);
    try {
      await axios.post(`${BACKEND_URL}/api/blog/fetch-news`);
      alert('Started fetching news from RSS feeds. Refresh in a few moments to see new posts.');
      setTimeout(fetchData, 5000);
    } catch (error) {
      console.error('Failed to fetch news:', error);
      alert('Failed to start news fetch');
    } finally {
      setFetching(false);
    }
  };

  const handlePublish = async (postId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/blog/posts/${postId}/publish`);
      fetchData();
    } catch (error) {
      console.error('Failed to publish post:', error);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/blog/posts/${postId}`);
      fetchData();
      setSelectedPost(null);
    } catch (error) {
      console.error('Failed to delete post:', error);
    }
  };

  const handleUpdateStatus = async (postId, status) => {
    try {
      await axios.put(`${BACKEND_URL}/api/blog/posts/${postId}`, { status });
      fetchData();
    } catch (error) {
      console.error('Failed to update post:', error);
    }
  };

  const filteredPosts = posts.filter(post => {
    if (filter === 'all') return true;
    return post.status === filter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'review': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Blog Manager</h1>
          <p className="text-gray-600">Auto-generate blog posts from dental news RSS feeds</p>
        </div>
        <button
          onClick={handleFetchNews}
          disabled={fetching}
          className="bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 disabled:bg-gray-400 flex items-center gap-2"
        >
          {fetching ? (
            <>
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
              </svg>
              Fetching...
            </>
          ) : (
            <>📰 Fetch Latest News</>
          )}
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
            <p className="text-gray-600 text-sm">Total Posts</p>
            <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-gray-400">
            <p className="text-gray-600 text-sm">Drafts</p>
            <p className="text-2xl font-bold text-gray-800">{stats.drafts}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
            <p className="text-gray-600 text-sm">In Review</p>
            <p className="text-2xl font-bold text-gray-800">{stats.in_review}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
            <p className="text-gray-600 text-sm">Published</p>
            <p className="text-2xl font-bold text-gray-800">{stats.published}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('posts')}
          className={`px-4 py-2 font-medium ${activeTab === 'posts' ? 'text-teal-600 border-b-2 border-teal-600' : 'text-gray-500'}`}
        >
          Blog Posts ({posts.length})
        </button>
        <button
          onClick={() => setActiveTab('feeds')}
          className={`px-4 py-2 font-medium ${activeTab === 'feeds' ? 'text-teal-600 border-b-2 border-teal-600' : 'text-gray-500'}`}
        >
          RSS Feeds ({feeds.length})
        </button>
      </div>

      {activeTab === 'posts' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Posts List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b flex justify-between items-center">
                <h2 className="font-semibold text-gray-800">Blog Posts</h2>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-1 text-sm"
                >
                  <option value="all">All Posts</option>
                  <option value="draft">Drafts</option>
                  <option value="review">In Review</option>
                  <option value="published">Published</option>
                </select>
              </div>
              <div className="divide-y max-h-[600px] overflow-y-auto">
                {filteredPosts.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <p>No posts found. Click "Fetch Latest News" to get started!</p>
                  </div>
                ) : (
                  filteredPosts.map((post) => (
                    <div
                      key={post.id}
                      onClick={() => setSelectedPost(post)}
                      className={`p-4 cursor-pointer hover:bg-gray-50 ${selectedPost?.id === post.id ? 'bg-teal-50' : ''}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-medium text-gray-800 line-clamp-2">{post.title}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(post.status)}`}>
                          {post.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mb-2">Source: {post.source_name}</p>
                      <div className="flex flex-wrap gap-1">
                        {post.tags?.slice(0, 3).map((tag, i) => (
                          <span key={i} className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Post Preview/Edit */}
          <div className="lg:col-span-1">
            {selectedPost ? (
              <div className="bg-white rounded-lg shadow p-4">
                <h2 className="font-semibold text-gray-800 mb-4">Post Details</h2>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-600">Title</label>
                    <p className="font-medium">{selectedPost.title}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Status</label>
                    <select
                      value={selectedPost.status}
                      onChange={(e) => handleUpdateStatus(selectedPost.id, e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                    >
                      <option value="draft">Draft</option>
                      <option value="review">In Review</option>
                      <option value="published">Published</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Summary</label>
                    <p className="text-sm text-gray-700">{selectedPost.summary}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Source</label>
                    <a href={selectedPost.source_url} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline text-sm block">
                      {selectedPost.source_name} ↗
                    </a>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">SEO Title</label>
                    <p className="text-sm">{selectedPost.seo_title}</p>
                  </div>
                  <div className="flex gap-2 pt-4 border-t">
                    {selectedPost.status !== 'published' && (
                      <button
                        onClick={() => handlePublish(selectedPost.id)}
                        className="flex-1 bg-green-600 text-white py-2 rounded hover:bg-green-700"
                      >
                        Publish
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(selectedPost.id)}
                      className="flex-1 bg-red-600 text-white py-2 rounded hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                <p>Select a post to view details</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'feeds' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h2 className="font-semibold text-gray-800">RSS Feed Sources</h2>
            <p className="text-sm text-gray-500">News is automatically fetched from these dental industry feeds</p>
          </div>
          <div className="divide-y">
            {feeds.map((feed) => (
              <div key={feed.id} className="p-4 flex justify-between items-center">
                <div>
                  <h3 className="font-medium text-gray-800">{feed.name}</h3>
                  <p className="text-sm text-gray-500">{feed.url}</p>
                  <p className="text-xs text-gray-400">
                    Category: {feed.category} | 
                    {feed.last_fetched ? ` Last fetched: ${new Date(feed.last_fetched).toLocaleDateString()}` : ' Never fetched'}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm ${feed.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                  {feed.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageBlog;
